import { cx } from '@lastbrand/web-ui-styled-system/css'

import { EDDMessageContainerDS } from '@src/components/blocks/edd-message-container-ds'
import {
  HolidayMessageDS,
  InStockAndReadyToShipDS,
  NonPhysicalProductDeliveryTextDS,
} from '@src/components/blocks/product-cta-ds/product-message-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { KlarnaWrapper } from '@src/quince-components/src/atoms/productForm/product-form-shared'
import Product from '@src/quince-site-common/src/domains/Product'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'

import { containerCss } from './product-button-bag-ds.styles'

import type { ReactNode } from 'react'

// Figma: Quince Design System 2.0 → Widgets → ProductButtonBag (node 8719:17323).
// Variant names match Figma. The variant is REQUIRED and computed by the caller — canonical
// derivation is `useProductButtonBagDSVariant` (used by `ProductCTADSWrapper`). It selects the
// message region this block renders above the CTA:
// `buttonOnly`      — no message.
// `stock`           — `InStockAndReadyToShipDS`.
// `stockEnterZip`   — EDD container, enter-zip branch (no zip captured).
// `stockValidZip`   — EDD container, ships-free-to-zip branch.
// `stockUpdateZip`  — EDD container, unable-to-ship branch.
// `email`           — `NonPhysicalProductDeliveryTextDS` (non-physical / gift-card delivery).
// `edd`             — EDD container, delivery-date + order-cutoff branch.
// `eddChristmas`    — `HolidayMessageDS` above the EDD row.
// `outOfStock` / `outOfStockGuest` / `waitlisted` / `preorder` — no block-level message; the red
//   OOS text, email input and button state render inside the CTA region (`ProductWaitlistCTADS` /
//   the CTA switch), which also owns the Figma button set (`default` / `applePay` / `shopApp`).
// Figma `hasCaption` ≈ `!hideKlarnaFull` (plus KlarnaWrapper's own salePrice/clientId gate);
// `quickAddToCart` / `breakpoint` are handled by the quick-ATC props and CSS breakpoints.
export type ProductButtonBagDSVariant =
  | 'buttonOnly'
  | 'stock'
  | 'stockEnterZip'
  | 'stockValidZip'
  | 'stockUpdateZip'
  | 'email'
  | 'edd'
  | 'eddChristmas'
  | 'outOfStock'
  | 'outOfStockGuest'
  | 'waitlisted'
  | 'preorder'

// Prop names are preserved verbatim from the pieces this block combines: the message region keeps
// the `EDDMessageContainerDS` / message-component vocabulary (`eddDetail`, `product`,
// `selectedInternalVariantId`, `showIcon`, `inStockMsgClass`, `showEDDMsg`), the Klarna region
// keeps the `KlarnaWrapper` vocabulary (`hidePayLater` / `festivalOfferPrice` /
// `klarnaWrapperClassname`) plus the form-level guard (`hideKlarnaFull`).
export interface ProductButtonBagDSProps {
  variant: ProductButtonBagDSVariant
  /** The CTA button region (ProductCTADS: buttons, waitlist input, ExpressPay, error messages). */
  children: ReactNode
  product?: Product
  eddDetail?: EDDDetails
  selectedInternalVariantId?: number[]
  isQuickATCForMobile?: boolean
  /**
   * Mounts the EDD container (the caller's `showEDDMsg` gate, incl. gold-bar/post-order checks).
   * Kept independent of `variant` because the container self-selects its branch AND fires its
   * EDD IS_VISIBLE telemetry even in states where it renders nothing — parity with the previous
   * `ProductCTADSWrapper` mounting.
   */
  showEDDMsg?: boolean
  /** Show the check icon beside the message (mobile) — same contract as the message components. */
  showIcon?: boolean
  inStockMsgClass?: string
  /** Raw (non-festival) sale price — the Klarna placement gates on this. */
  salePrice?: number
  /** Festival offer price — what the Klarna amount uses when set. */
  festivalOfferPrice?: number
  /** `KlarnaWrapper.hidePayLater` — hides the pay-later placement only. */
  hidePayLater?: boolean
  /** Form-level guard (`use-product-form`'s `hideKlarnaFull`) — suppresses the Klarna region entirely. */
  hideKlarnaFull?: boolean
  /** `KlarnaWrapper.wrapperClassname` passthrough. */
  klarnaWrapperClassname?: string
  className?: string
}

/**
 * ProductButtonBagDS — the DS 2.0 buy-box block: in-stock/EDD message, CTA button and Klarna
 * message as one unit (Figma ProductButtonBag). The `variant` drives the message region; the CTA
 * region is passed as children by the state-owning caller (`ProductCTADSWrapper`) so click
 * handling, ExpressPay and telemetry stay where they live today.
 */
const ProductButtonBagDS = ({
  variant,
  children,
  product,
  eddDetail,
  selectedInternalVariantId,
  isQuickATCForMobile,
  showEDDMsg,
  showIcon,
  inStockMsgClass,
  salePrice,
  festivalOfferPrice,
  hidePayLater,
  hideKlarnaFull,
  klarnaWrapperClassname,
  className,
}: ProductButtonBagDSProps) => {
  return (
    <div data-testid={TEST_LOCATORS.PRODUCT_BUTTON_BAG} data-variant={variant} className={cx(containerCss, className)}>
      {variant === 'eddChristmas' ? <HolidayMessageDS className={inStockMsgClass} eddDetail={eddDetail} /> : null}
      {showEDDMsg && product ? (
        <EDDMessageContainerDS
          eddDetail={eddDetail}
          product={product}
          selectedInternalVariantId={selectedInternalVariantId}
          isQuickATCForMobile={isQuickATCForMobile}
          showIcon={showIcon}
          holidayMessageShown={variant === 'eddChristmas'}
        />
      ) : null}
      {variant === 'email' ? <NonPhysicalProductDeliveryTextDS className={inStockMsgClass} /> : null}
      {variant === 'stock' ? <InStockAndReadyToShipDS className={inStockMsgClass} /> : null}
      {children}
      {!hideKlarnaFull ? (
        <KlarnaWrapper
          hidePayLater={hidePayLater}
          salePrice={salePrice}
          festivalOfferPrice={festivalOfferPrice}
          wrapperClassname={klarnaWrapperClassname}
        />
      ) : null}
    </div>
  )
}

export default ProductButtonBagDS
